import React from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import {
  Text, ArticleTeaser, Pagination, HeroBanner
} from '@comicrelief/component-library';
import { getImage } from 'gatsby-plugin-image';

import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import NewsFilter from '../../components/NewsFilter/NewsFilter';
import seoDefaultImage from '../../../static/assets/news-default.jpg';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import {
  Wrapper, Container, Section, Grid, Items
} from './Categories.styles';

const handleSelect = () => {
  window.scrollTo({ top: 100, behavior: 'smooth' });
};

const handlePaginationUrl = (page, category) => {
  const categoryPath = kebabCase(category);
  if (page === 1) {
    return `/news/${categoryPath}`;
  }
  return `/news/${categoryPath}/page-${page}`;
};

const Categories = (props) => {
  const articles = get(props, 'data.allContentfulPageArticle.edges');
  const pageInfo = get(props, 'data.allContentfulPageArticle.pageInfo');
  const category = get(props, 'pageContext.category');

  // To match the new IA (Information Architecture) from Numiko:
  const parentPages = [{ title: 'About Us', path: 'about' }];
  const thisPageTitle = 'News';

  // Handle the edgecase so we don't end up with a goofy 'NEWS: NEWS' title:
  const extendedPageTitle = `${thisPageTitle}${category !== 'news' ? `: ${category}` : ''}`;

  return (
    <Layout>
      <Wrapper>

        <Breadcrumb
          thisPageTitle={thisPageTitle}
          parentPages={parentPages}
        />

        <HeroBanner
          pageBackgroundColour="grey_light"
          textBannerCopyBackgroundColour="blue"
          variant="text_banner"
          copyColour="white"
          paddingTop="0rem"
          paddingBottom="0rem"
        >
          <Text tag="h1">
            {extendedPageTitle}
          </Text>
        </HeroBanner>

        <Container>
          <Section>
            <NewsFilter
              selectedCategory={category}
            />
            <Grid>
              {articles.map(({ node }) => {
                const {
                  image, path, displayDate, title, id
                } = node;

                const thisImageData = getImage(image);
                const thisImageFallback = thisImageData?.images?.fallback?.src || seoDefaultImage;
                const thisImageSrcSet = thisImageData?.images?.fallback?.srcSet
                              || thisImageData?.images?.sources?.[0]?.srcSet || seoDefaultImage;
                const thisImageLow = thisImageData?.placeholder?.fallback || seoDefaultImage;
                const thisImageDesc = image?.description ?? '';
                const URL = `/news/${path}`;

                return (
                  <Items key={id}>
                    <ArticleTeaser
                      href={URL}
                      image={thisImageFallback}
                      images={thisImageSrcSet}
                      imageLow={thisImageLow}
                      date={displayDate}
                      title={title}
                      alt={thisImageDesc}
                    />
                  </Items>
                );
              })}
            </Grid>
          </Section>
          {pageInfo.pageCount > 1 ? (
            <div data-test="news-pagination">
              <Pagination
                disabledBackgroundColor="grey_dark"
                disabledColor="grey_light"
                maxPages={10}
                totalPages={pageInfo.pageCount}
                currentPage={pageInfo.currentPage}
                createURL={value => handlePaginationUrl(value, category)}
                onSelect={handleSelect}
                showFirst
                showPrevious={false}
                showNext={false}
                showLast
              />
            </div>
          ) : null}
        </Container>
      </Wrapper>
    </Layout>
  );
};

export default Categories;

export const Head = props => (
  <SEO
    title="News"
    allData={props}
    pageType={SEO_PAGE_TYPES.ARTICLES_CATEGORY}
  >
    {(siteUrl, linkedDataObj) => (
      <script type="application/ld+json" key="carousel">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: linkedDataObj?.theseArticleEdges.map((article, position) => ({
            '@type': 'ListItem',
            position,
            url: `${siteUrl}/news/${article.node.path}`
          }))
        })}
      </script>
    )}
  </SEO>
);

export const pageQuery = graphql`
  query getCategoryArticles($skip: Int, $limit: Int, $category: String) {
    allContentfulPageArticle(
      skip: $skip
      limit: $limit
      filter: {type: {eq: "News Article"}, categories: {elemMatch: {title: {eq: $category}}}}
      sort: {displayDate: DESC}
    ) {
      totalCount
      pageInfo {
        perPage
        currentPage
        hasPreviousPage
        hasNextPage
        itemCount
        pageCount
      }
      edges {
        node {
          title
          path
          type
          id
          categories {
            title
          }
          displayDate(formatString: "Do MMMM YYYY")
          image {
            ...Picture
          }
        }
      }
    }
    allContentfulPageLandingPage(filter: {path: {eq: "news-seo"}}) {
      edges {
        node {
          path
          title
          summary {
            summary
          }
        }
      }
    }
  }
`;
